import { z } from 'zod';

/**
 * @aiPurpose kind discriminator for lead communications (#1807).
 * @aiInvariant append-only; values are stored in rows forever. Mirrored by the
 *   Prisma `LeadCommunicationKind` enum in
 *   `apps/backend/prisma/schema/lead-communications.prisma`.
 */
export enum LeadCommunicationKind {
  CALL = 'CALL',
  MESSAGE = 'MESSAGE',
}
export const LeadCommunicationKindSchema = z.nativeEnum(LeadCommunicationKind);

/**
 * @aiPurpose direction of the communication relative to the organisation (#1807).
 * @aiInvariant append-only. Mirrored by `LeadCommunicationDirection` in Prisma.
 */
export enum LeadCommunicationDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}
export const LeadCommunicationDirectionSchema = z.nativeEnum(LeadCommunicationDirection);

/**
 * @aiPurpose channel over which the communication happened (#1807).
 * @aiInvariant minimal, append-only set — do not add speculative channels.
 *   Mirrored by `LeadCommunicationChannel` in Prisma.
 */
export enum LeadCommunicationChannel {
  PHONE = 'PHONE',
  WHATSAPP = 'WHATSAPP',
  TELEGRAM = 'TELEGRAM',
}
export const LeadCommunicationChannelSchema = z.nativeEnum(LeadCommunicationChannel);

/**
 * @aiPurpose CRM Lead communication payload constants (#1807). The 4000-char
 *   cap mirrors the persistence column and the `LeadComment` body cap.
 */
export const LEAD_COMMUNICATION_TEXT_MIN = 1;
export const LEAD_COMMUNICATION_TEXT_MAX = 4000;

const LeadCommunicationTextSchema = z
  .string()
  .min(LEAD_COMMUNICATION_TEXT_MIN)
  .max(LEAD_COMMUNICATION_TEXT_MAX)
  // Reject all-whitespace text at the wire layer — mirrors the LeadComment body guard.
  .refine(
    (s) => s.trim().length >= LEAD_COMMUNICATION_TEXT_MIN,
    'communication text must not be whitespace-only',
  );

/**
 * @aiPurpose author reference attached to a communication row at read time.
 * @aiPii `name` is the display name from the User row. Visibility is already
 *   enforced by the parent Lead-scope gate.
 */
export const LeadCommunicationAuthorRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type LeadCommunicationAuthorRef = z.infer<typeof LeadCommunicationAuthorRefSchema>;

/**
 * @aiPurpose request envelope for POST `/leads/:id/communications`.
 * @aiInvariant `.strict()` rejects unknown keys so typos surface as 400.
 * @aiPii `text` is PII (call notes / message body). `transcript` is
 *   RESERVED and intentionally omitted from input — the future AI layer
 *   populates it; clients must never submit it.
 */
export const CreateLeadCommunicationRequestSchema = z
  .object({
    kind: LeadCommunicationKindSchema,
    direction: LeadCommunicationDirectionSchema,
    channel: LeadCommunicationChannelSchema,
    occurredAt: z.string().datetime(),
    text: LeadCommunicationTextSchema,
  })
  .strict();
export type CreateLeadCommunicationRequest = z.infer<typeof CreateLeadCommunicationRequestSchema>;

/**
 * @aiPurpose wire projection of a single `LeadCommunication` row.
 * @aiPii `text` and `transcript` are PII. Surfaced only to actors that pass
 *   the parent Lead-visibility gate.
 */
export const LeadCommunicationSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    kind: LeadCommunicationKindSchema,
    direction: LeadCommunicationDirectionSchema,
    channel: LeadCommunicationChannelSchema,
    author: LeadCommunicationAuthorRefSchema.nullable(),
    occurredAt: z.string().datetime(),
    text: z.string(),
    transcript: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type LeadCommunication = z.infer<typeof LeadCommunicationSchema>;

export const LeadCommunicationResponseSchema = z
  .object({ communication: LeadCommunicationSchema })
  .strict();
export type LeadCommunicationResponse = z.infer<typeof LeadCommunicationResponseSchema>;

/**
 * @aiPurpose feed-reference projection of a `LeadCommunication` row. Embedded
 *   in `LeadActivityReferences.communication` when a CALL or MESSAGE activity
 *   row resolves its referenced communication. Full text is included because
 *   the entire feed is visibility-gated and there is no separate read endpoint.
 * @aiPii `text` and `transcript` are PII — only surfaced inside the
 *   visibility-gated lead-activity feed.
 */
export const LeadCommunicationReferenceSchema = z
  .object({
    id: z.string().uuid(),
    kind: LeadCommunicationKindSchema,
    direction: LeadCommunicationDirectionSchema,
    channel: LeadCommunicationChannelSchema,
    occurredAt: z.string().datetime(),
    text: z.string(),
    transcript: z.string().nullable(),
  })
  .strict();
export type LeadCommunicationReference = z.infer<typeof LeadCommunicationReferenceSchema>;
